import React from 'react';
import styled from 'styled-components';
import { Colors } from '../constants/colors';
import logo from '../../assets/images/mild-blue-white.svg';
import { Device } from '../constants/device';
import { OpenInNewIcon } from './icons/OpenInNewIcon';
import { ArrowRightDiagonal } from './icons/ArrowRightDiagonal';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { localizedPath } from '../helpers/localizedPath';
import { CONSULTING_PATH, TRAINING_PATH } from '../constants/routes';
import { scrollToTop } from '../helpers/scrollToTop';
import { showsIkemTraining } from '../helpers/showsIkemTraining';
import { useUseCase } from '../helpers/useUseCase';
import { hasIkemTraining } from '../config/useCasesConfig';

export const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const docs = t('footer.documents.items', { returnObjects: true }) as {
    title: string;
    url: string;
  }[];
  const year = new Date().getFullYear();
  const showTraining = showsIkemTraining(i18n.language) && hasIkemTraining(useUseCase());

  return (
    <StyledFooter>
      <LinkGroups>
        <LinkGroup>
          <BoldLabel>{t('footer.pages.label')}</BoldLabel>
          <LinkList>
            <LinkItem>
              <FooterLink to={localizedPath(pathname, CONSULTING_PATH)} onClick={scrollToTop}>
                {t('consulting.nav')}
              </FooterLink>
              <ArrowRightDiagonal />
            </LinkItem>
            {showTraining && (
              <LinkItem>
                <FooterLink to={localizedPath(pathname, TRAINING_PATH)} onClick={scrollToTop}>
                  {t('training.nav')}
                </FooterLink>
                <ArrowRightDiagonal />
              </LinkItem>
            )}
          </LinkList>
        </LinkGroup>
        {docs.length > 0 && (
          <LinkGroup>
            <BoldLabel>{t('footer.documents.label')}</BoldLabel>
            <LinkList>
              {docs.map((doc) => (
                <LinkItem key={doc.title}>
                  <a href={doc.url} target="_blank" rel="noreferrer">
                    {doc.title}
                  </a>
                  <OpenInNewIcon />
                </LinkItem>
              ))}
            </LinkList>
          </LinkGroup>
        )}
      </LinkGroups>
      <CompanyInfoContainer>
        <CompanyLogo>
          <LogoHolder
            onClick={() => {
              window.open('https://mild.blue/', '_blank')?.focus();
            }}
          >
            <img src={logo} alt={'mild.blue logo'} />
          </LogoHolder>
          <p>{t('footer.slogan')}</p>
        </CompanyLogo>
        <Divider hideOnTablet />
        <AddressContainer>
          <Address>
            <BoldLabel>{t('footer.contactAddress')}</BoldLabel>
            <div>slp.blue s.r.o.</div>
            <div>Plzeňská 168/27</div>
            <div>150 00 Praha 5</div>
          </Address>
          <Divider />
          <Address>
            <BoldLabel>{t('footer.billingAddress')}</BoldLabel>
            <div>slp.blue s.r.o.</div>
            <div>Brigádníků 3022/285</div>
            <div>100 00 Praha 10</div>
            <div>IČ: 19751567</div>
          </Address>
        </AddressContainer>
      </CompanyInfoContainer>
      <FinancingContainer>
        {t('footer.fundingNote')}
        <FinancingLogos>
          <img src={'/ngeu_logo.png'} alt={'ngeu_logo'} height={'40px'} />
          <img src={'/npo_logo.png'} alt={'npo_logo'} height={'40px'} />
        </FinancingLogos>
      </FinancingContainer>
      <CopyrightContainer>
        <div>slp.blue®</div>
        <div>{`© ${year} slp.blue s.r.o.`}</div>
      </CopyrightContainer>
    </StyledFooter>
  );
};

const BoldLabel = styled.div`
  font-weight: bold;
`;

const LinkGroups = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 80px;

  ${Device.tablet} {
    flex-direction: column;
    align-items: center;
    gap: 30px;
  }
`;

const LinkGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const LinkItem = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;

  a {
    color: ${Colors.white};
    text-decoration: none;
  }

  svg {
    width: 20px;
    height: 20px;
    fill: ${Colors.white};
    flex: none;
  }

  &:hover a,
  a:focus-visible {
    text-decoration: underline;
  }

  ${Device.mobile} {
    a {
      text-decoration: underline;
    }

    svg {
      display: none;
    }
  }
`;

const FooterLink = styled(Link)``;

const StyledFooter = styled.footer`
  background: ${Colors.primary};
  color: ${Colors.white};
  padding: 60px 20px 30px;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  gap: 40px;
`;

const CompanyInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 30px;

  ${Device.tablet} {
    flex-direction: column;
    align-items: center;
  }
`;

const CompanyLogo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 200px;

  img {
    max-width: 120px;
  }

  p {
    color: ${Colors.primaryLight};
    margin-bottom: 0;
  }
`;

const LogoHolder = styled.div`
  cursor: pointer;
`;

const AddressContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: center;
  gap: 30px;

  ${Device.tablet} {
    width: 100%;
    gap: 10px;
  }
`;

const Address = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 200px;
  text-align: start;

  ${Device.mobile} {
    width: none;
  }
`;

const Divider = styled.div<{ hideOnTablet?: boolean }>`
  background: ${Colors.primaryLight};
  height: 50px;
  width: 2px;
  border-radius: 5px;
  margin-top: 25px;

  ${Device.tablet} {
    display: ${(props) => (props.hideOnTablet ? 'none' : 'flex')};
  }
`;

const FinancingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
`;

const FinancingLogos = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 100px;

  ${Device.mobile} {
    gap: 30px;
  }
`;

const CopyrightContainer = styled.div`
  text-align: center;
  font-size: 12px;
  opacity: 0.5;
`;
